<template>
    <div class="campaign-builder-modal">
        <GoalModal
            data-qa="internal-form-modal-goal"
            :is-open="isOpen"
            size="med"
            :action-button-label="$t('funnel.goal.internal.form.edit')"
            action-button-trailing-icon="external-link"
            :summary-text="$t('funnel.goal.internal.form.summary')"
            :title="$t('funnel.goal.internal.form.title')"
            :goal-tip-text="$t('funnel.goal.internal.form.pro.tip')"
            :goal-tip-link="$options.PRO_TIP_LINK"
            @close-modal="closeModal"
            @update-config="editOrCreateInternalForm"
        />
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import {
    createInternalForm,
    saveInternalFormOptions,
} from './internal.form.api';

export const PRO_TIP_LINK = 'https://help.infusionsoft.com/help/create-an-internal-form';
export const FORM_NAME = 'Internal Form submitted';
export const FORM_TYPE = 'internalForm';
export const EVENT_READY_TOGGLE_CHANGED = 'EVENT_READY_TOGGLE_CHANGED';
export const EVENT_EDITOR_LOADED = 'EVENT_EDITOR_LOADED';
export const EVENT_CONFIG_DATA = 'EVENT_CONFIG_DATA';
export const EVENT_SAVE_CONFIG_DATA = 'EVENT_SAVE_CONFIG_DATA';
export const EVENT_CLOSE_EDITOR = 'EVENT_CLOSE_EDITOR';
export const EVENT_RENAME = 'EVENT_RENAME';


export default {
    EVENT_READY_TOGGLE_CHANGED,
    EVENT_EDITOR_LOADED,
    EVENT_SAVE_CONFIG_DATA,
    EVENT_CONFIG_DATA,
    EVENT_CLOSE_EDITOR,
    EVENT_RENAME,
    FORM_NAME,
    FORM_TYPE,
    PRO_TIP_LINK,
    components: {
        GoalModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        funnelId: {
            type: [String, Number],
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    internalFormId: '',
                },
            }),
        },
    },

    emits: ['close-modal', 'update-cell-value', 'update-cell-name'],

    data() {
        return {
            internalFormId: '',
            name: '',
            editorsTab: null,
            isReady: false,
        };
    },

    computed: {
        isConfigured() {
            return !!this.internalFormId;
        },
    },

    mounted() {
        this.addEventListeners();
        this.internalFormId = this.cell?.value?.internalFormId || '';
        this.name = this.cell?.value?.name || FORM_NAME;
        this.isReady = this.cell.value?.ready === '1' ?? false;
    },

    unmounted() {
        this.removeEventListeners();
    },

    methods: {
        addEventListeners() {
            window.addEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.addEventListener(EVENT_SAVE_CONFIG_DATA, this.saveConfigData);
            window.addEventListener(EVENT_EDITOR_LOADED, this.editorReady);
            window.addEventListener(EVENT_RENAME, this.renameForm);
        },

        removeEventListeners() {
            window.addEventListener(EVENT_READY_TOGGLE_CHANGED, this.readyToggleChanged);
            window.addEventListener(EVENT_SAVE_CONFIG_DATA, this.saveConfigData);
            window.removeEventListener(EVENT_EDITOR_LOADED, this.editorReady);
            window.removeEventListener(EVENT_RENAME, this.renameForm);
        },

        readyToggleChanged() {
            this.isReady = !this.isReady;

            this.saveCell();
        },

        async saveConfigData({ detail }) {
            await this.saveConfigOptions(detail.selectedDupCheckOption);
        },

        renameForm({ detail }) {
            const cell = { ...this.cell };

            cell.value = cell.value || {};
            cell.value.name = detail.name;

            this.$emit('update-cell-name', { cell });
        },

        saveCell() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};
            cell.value.internalFormId = this.internalFormId;

            this.$emit('update-cell-value', { cell, isReady: this.isReady, preventClose: true });
        },


        editorReady() {
            this.postConfigMessage();
        },

        postConfigMessage() {
            this.editorsTab.postMessage({
                eventType: EVENT_CONFIG_DATA,
                data: {
                    cellName: this.name,
                    isReady: this.isReady,
                },
            }, '*');
        },

        closeModal() {
            if (this.editorsTab && !this.editorsTab.closed) {
                this.editorsTab.focus();
            } else {
                if (this.isConfigured) {
                    this.saveCell();
                }

                this.removeEventListeners();
                this.$emit('close-modal');
            }
        },

        async saveConfigOptions(dupCheckOptionString) {
            await saveInternalFormOptions(this.internalFormId, this.funnelId, dupCheckOptionString);
        },

        async editOrCreateInternalForm() {
            try {
                if (!this.internalFormId) {
                    this.internalFormId = await this.createInternalForm();
                    this.isReady = true;
                }

                this.editInternalForm();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Could not create an internal form: ${error}`);
            }
        },

        async createInternalForm() {
            return createInternalForm(this.funnelId, this.name);
        },

        editInternalForm() {
            if (this.editorsTab && !this.editorsTab.closed) {
                this.editorsTab.focus();
            } else {
                const editorUrl = `/app/internalFormFunnel/standAloneEditor?contentType=${FORM_TYPE}&parentId=${this.internalFormId}`;

                this.editorsTab = window.open(editorUrl, '_blank');
            }
        },
    },
};
</script>
